import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';

/**
 * Get current time in a specific timezone
 */
export function getCurrentTimeInZone(timezone: string): Date {
  return utcToZonedTime(new Date(), timezone);
}

/**
 * Format time as HH:MM:SS
 */
export function formatTime(
  date: Date,
  timezone: string,
  options: { use24Hour?: boolean; showSeconds?: boolean } = {}
): string {
  const { use24Hour = true, showSeconds = true } = options;
  
  const format = use24Hour
    ? showSeconds ? 'HH:mm:ss' : 'HH:mm'
    : showSeconds ? 'hh:mm:ss a' : 'hh:mm a';
  
  return formatInTimeZone(date, timezone, format);
}

/**
 * Format date as "Tuesday, January 14, 2026"
 */
export function formatDate(date: Date, timezone: string): string {
  return formatInTimeZone(date, timezone, 'EEEE, MMMM d, yyyy');
}

/**
 * Get current hour (0-23) in a timezone
 */
export function getCurrentHourInZone(timezone: string): number {
  return parseInt(formatInTimeZone(new Date(), timezone, 'H'), 10);
}

/**
 * Get work status based on current hour and work hours
 */
export type WorkStatus = 'working' | 'soon' | 'offline' | 'sleeping';

export function getWorkStatus(
  currentHour: number,
  workStart: number,
  workEnd: number
): WorkStatus {
  // Sleeping: 22:00 - 06:00 (regardless of work hours)
  if (currentHour >= 22 || currentHour < 6) {
    return 'sleeping';
  }
  
  // Working: within work hours
  if (currentHour >= workStart && currentHour < workEnd) {
    return 'working';
  }
  
  // Soon: within 1 hour of work start or end
  if (currentHour === workStart - 1 || currentHour === workEnd) {
    return 'soon';
  }
  
  // Offline: outside work hours but not sleeping
  return 'offline';
}

/**
 * Get status emoji based on work status
 */
export function getStatusEmoji(status: WorkStatus): string {
  switch (status) {
    case 'working': return '🟢';
    case 'soon': return '🟡';
    case 'offline': return '🟠';
    case 'sleeping': return '💤';
  }
}

/**
 * Get status label
 */
export function getStatusLabel(
  status: WorkStatus,
  currentHour: number,
  workStart: number,
  workEnd: number
): string {
  switch (status) {
    case 'working': {
      const hoursLeft = workEnd - currentHour;
      return `Working • ${hoursLeft}h left`;
    }
    case 'soon': {
      if (currentHour < workStart) {
        return `Starting in 1h`;
      }
      return `Just ended`;
    }
    case 'offline': {
      if (currentHour < workStart) {
        const hoursUntil = workStart - currentHour;
        return `Work in ${hoursUntil}h`;
      }
      return `Off hours`;
    }
    case 'sleeping': {
      let hoursUntilWork: number;
      if (currentHour >= 22) {
        hoursUntilWork = (24 - currentHour) + workStart;
      } else {
        hoursUntilWork = workStart - currentHour;
      }
      return `Sleeping • ${hoursUntilWork}h until work`;
    }
  }
}

/**
 * Convert work hours from one timezone to another
 * Returns [start, end] in target timezone (may span midnight)
 */
export function convertWorkHours(
  workStart: number,
  workEnd: number,
  fromTimezone: string,
  toTimezone: string
): [number, number] {
  // Create a reference date with work start time in source timezone
  const refDate = new Date();
  refDate.setHours(workStart, 0, 0, 0);
  
  // Get the time in source timezone
  const sourceDate = utcToZonedTime(refDate, fromTimezone);
  sourceDate.setHours(workStart, 0, 0, 0);
  
  // Convert to target timezone
  const targetStartStr = formatInTimeZone(sourceDate, toTimezone, 'H');
  const targetStart = parseInt(targetStartStr, 10);
  
  // Same for end time
  sourceDate.setHours(workEnd, 0, 0, 0);
  const targetEndStr = formatInTimeZone(sourceDate, toTimezone, 'H');
  const targetEnd = parseInt(targetEndStr, 10);
  
  return [targetStart, targetEnd];
}

/**
 * Calculate overlap between two sets of work hours
 * Returns hours of overlap
 */
export function calculateOverlap(
  person1: { workStart: number; workEnd: number },
  person2: { workStart: number; workEnd: number }
): number {
  const start = Math.max(person1.workStart, person2.workStart);
  const end = Math.min(person1.workEnd, person2.workEnd);
  
  if (start >= end) return 0;
  return end - start;
}

/**
 * Find best overlap times for a group of people
 * Returns array of [startHour, endHour] in reference timezone
 */
export function findBestOverlapTimes(
  people: Array<{
    workStart: number;
    workEnd: number;
    timezone: string;
  }>,
  referenceTimezone: string
): Array<{ start: number; end: number; count: number }> {
  if (people.length === 0) return [];
  
  // Convert all work hours to reference timezone
  const workHoursInRef = people.map((p) => {
    const [start, end] = convertWorkHours(
      p.workStart,
      p.workEnd,
      p.timezone,
      referenceTimezone
    );
    return { start, end };
  });
  
  // Find overlapping periods
  const overlaps: Array<{ start: number; end: number; count: number }> = [];
  
  // Check each hour of the day
  for (let hour = 0; hour < 24; hour++) {
    const workingCount = workHoursInRef.filter((wh) => {
      if (wh.start < wh.end) {
        return hour >= wh.start && hour < wh.end;
      } else {
        // Spans midnight
        return hour >= wh.start || hour < wh.end;
      }
    }).length;
    
    if (workingCount >= 2) {
      const lastOverlap = overlaps[overlaps.length - 1];
      if (lastOverlap && lastOverlap.end === hour && lastOverlap.count === workingCount) {
        lastOverlap.end = hour + 1;
      } else {
        overlaps.push({ start: hour, end: hour + 1, count: workingCount });
      }
    }
  }
  
  // Sort by count (most people first)
  return overlaps.sort((a, b) => b.count - a.count);
}

/**
 * Get timezone offset string (e.g., "+8:00" or "-5:00")
 */
export function getTimezoneOffset(timezone: string): string {
  const now = new Date();
  const offset = formatInTimeZone(now, timezone, 'xxx');
  return offset;
}

/**
 * Get timezone abbreviation (e.g., "CST", "EST")
 */
export function getTimezoneAbbr(timezone: string): string {
  const now = new Date();
  return formatInTimeZone(now, timezone, 'zzz');
}

/**
 * Common timezones for dropdown
 */
export const commonTimezones = [
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
];

/**
 * Emoji avatars for selection
 */
export const avatarEmojis = [
  '😀', '😎', '🤓', '🧑‍💻', '👩‍💻', '👨‍💻', 
  '🦊', '🐱', '🐶', '🐼', '🦁', '🐯',
  '🌟', '⭐', '🚀', '🎯', '💎', '🔥',
  '🌈', '🌙', '☀️', '🌸', '🍀', '🎨',
];
